import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  Box,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import LanguageSelectionStep from './LanguageSelectionStep';
import { User } from 'firebase/auth';
import { authService, isFirebaseInitialized } from '@watchlist/shared';

export interface OnboardingData {
  languages: string[];
  moviePreferences: {
    watchedMovies: string[];
    watchlistMovies: string[];
    dislikedMovies: string[];
    ratings: { [movieId: string]: number };
    movieDetails: { [movieId: string]: { title: string; poster?: string; year?: string } };
  };
}

interface OnboardingFlowProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  user: User;
}

// Helper function to calculate onboarding progress
export const calculateOnboardingProgress = (preferences: any, userMovieData?: any): number => {
  let progress = 0;

  // Step 1: Language selection (25%)
  if (preferences?.preferredLanguages && preferences.preferredLanguages.length > 0) {
    progress += 25;
  }

  // Step 2: Movie preferences (75% - based on number of movies rated/added)
  const watchedCount = userMovieData?.watchedMovies?.length || 0;
  const watchlistCount = userMovieData?.watchlistMovies?.length || 0;
  const totalMovies = watchedCount + watchlistCount;

  if (totalMovies > 0) {
    // Give credit for each movie, up to 8 movies for full step completion
    const movieProgress = Math.min(totalMovies / 8, 1) * 75;
    progress += movieProgress;
  }

  return Math.round(progress);
};

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({
  open,
  onClose,
  onComplete,
  user,
}) => {
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    languages: [],
    moviePreferences: {
      watchedMovies: [],
      watchlistMovies: [],
      dislikedMovies: [],
      ratings: {},
      movieDetails: {},
    },
  });

  // Load existing user preferences on mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        // Check if Firebase is properly initialized before attempting operations
        if (!isFirebaseInitialized()) {
          console.warn('OnboardingFlow: Firebase not yet initialized, skipping user preferences load');
          return;
        }

        const userData = await authService.getUserPreferences(user);
        if (userData?.preferredLanguages && userData.preferredLanguages.length > 0) {
          setOnboardingData(prev => ({
            ...prev,
            languages: userData.preferredLanguages || []
          }));
        }
      } catch (error) {
        console.log('No existing preferences found, using defaults');
      }
    };

    loadUserPreferences();
  }, [user.uid]);

  const handleLanguageSelection = useCallback(async (languages: string[]) => {
    console.log('🎬 OnboardingFlow: Selected languages:', languages);

    // Save language preferences immediately - create document if it doesn't exist
    try {
      const existingPreferences = await authService.getUserPreferences(user);
      console.log('🔍 OnboardingFlow: Existing preferences before save:', existingPreferences);

      if (existingPreferences) {
        console.log('🔄 OnboardingFlow: Updating preferences with languages:', languages);
        await authService.updateUserPreferences(user.uid, {
          preferredLanguages: languages,
          onboardingCompleted: true // Mark as completed after language selection
        });
      } else {
        console.log('🆕 OnboardingFlow: Creating new preferences with languages:', languages);
        // Document doesn't exist, create it
        await authService.createUserPreferences(user, {
          preferredLanguages: languages,
          onboardingCompleted: true // Mark as completed after language selection
        });
      }
      console.log('✅ Language preferences saved and onboarding marked complete');

      // Verify the save
      const verifyPrefs = await authService.getUserPreferences(user);
      console.log('✔️ OnboardingFlow: Verified saved preferences:', verifyPrefs);
    } catch (error) {
      console.error('❌ Failed to save language preferences:', error);
    }

    setOnboardingData(prev => ({ ...prev, languages }));

    // Complete onboarding
    onComplete();
  }, [user.uid, onComplete]);

  const handleSkip = async () => {
    if (!user) return;

    try {
      console.log('🔄 OnboardingFlow: Skipping onboarding, saving current data...', { languages: onboardingData.languages });

      // Set onboarding as skipped (so profile can show partial completion)
      try {
        const existingPreferences = await authService.getUserPreferences(user);
        if (existingPreferences) {
          await authService.updateUserPreferences(user.uid, {
            preferredLanguages: onboardingData.languages.length > 0 ? onboardingData.languages : ['en'],
            onboardingCompleted: false,
            onboardingSkipped: true,
          });
        } else {
          // Document doesn't exist, create it
          await authService.createUserPreferences(user, {
            preferredLanguages: onboardingData.languages.length > 0 ? onboardingData.languages : ['en'],
            onboardingCompleted: false,
            onboardingSkipped: true,
          });
        }
      } catch (skipError) {
        console.warn('⚠️ OnboardingFlow: Error updating skip status, creating new document:', skipError);
        await authService.createUserPreferences(user, {
          preferredLanguages: onboardingData.languages.length > 0 ? onboardingData.languages : ['en'],
          onboardingCompleted: false,
          onboardingSkipped: true,
        });
      }

      console.log('✅ OnboardingFlow: Language preferences saved:', onboardingData.languages.length > 0 ? onboardingData.languages : ['en']);
      console.log('✅ Onboarding skipped, preferences updated');
      onComplete(); // Go to homepage

    } catch (error) {
      console.error('❌ Error skipping onboarding:', error);
      onComplete(); // Still go to homepage even if save failed
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          bgcolor: 'rgba(0, 0, 0, 0.9)',
        },
      }}
    >
      <LanguageSelectionStep
        initialLanguages={onboardingData.languages}
        onComplete={handleLanguageSelection}
        onSkip={handleSkip}
      />
    </Dialog>
  );
};

export default OnboardingFlow;